import { Role } from '@/services/authentication/authentication.types';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiPlus, FiTrash } from 'react-icons/fi';
import { ContactInfo, ContactInfoType, OptionalContactInfo } from '../../../../components/form/OptionalContactInfo';
import { PhoneNumberField, PhoneNumberValue } from '../../../../components/form/PhoneNumberField';
import { SelectFieldCustom } from '../../../../components/form/SelectFieldCustom';
import { TextFieldWithOptionalLabel } from '../../../../components/form/TextInput';
import { IconButton } from '../../../../components/shared/button/IconButton';
import { BigCheckbox } from '../../../../components/shared/checkboxes/BigCheckbox';
import { validateEmail } from '../../../../helpers/Utility';
import { validatePhone } from '../../../../helpers/phoneHelper';
import strings from '../../team.translations';
import { RoleOption } from './AddMultipleTeamMembersForm';

const roleOptions: RoleOption[] = [
  { id: Role.Staff, name: strings.common.roles.staff },
  { id: Role.Manager, name: strings.common.roles.manager },
  { id: Role.Admin, name: strings.common.roles.admin }
];

export const AddTeamMemberRow: FC<{
  idx: number;
  methods: any;
  handleDelete: () => void;
  handleAdd: () => void;
}> = ({ idx, methods, handleDelete, handleAdd }) => {
  const [contactInfoType, setContactInfoType] = useState<ContactInfoType>(ContactInfo.email);
  const { getValues } = useFormContext();

  const searchDuplicate = (field: string, eleId: number): boolean => {
    const allValues = getValues();
    const targetVal = allValues.members[eleId][field];
    return !allValues.members.some(
      (ele: any, i: number) => i !== eleId && ele[field] !== undefined && ele[field] === targetVal
    );
  };

  const memberCount = methods.watch('members').length;

  return (
    <>
      {/* @ts-ignore */}
      <TextFieldWithOptionalLabel
        name={`members[${idx}].firstName`}
        placeholder={strings.addTeamMemberForm.firstNamePlaceholder}
        validationMessage={strings.common.mandatory(strings.addTeamMemberForm.firstName)}
        required
        className="min-w-[80px] sm:min-w-0"
      />

      {/* @ts-ignore */}
      <TextFieldWithOptionalLabel
        name={`members[${idx}].lastName`}
        placeholder={strings.addTeamMemberForm.lastNamePlaceholder}
        validationMessage={strings.common.mandatory(strings.addTeamMemberForm.lastName)}
        required
        className="min-w-[80px] sm:min-w-0"
      />

      <div className="flex space-x-4 mb-2">
        <OptionalContactInfo
          setContactTypeCallback={setContactInfoType}
          selectedContactType={contactInfoType}
          index={idx}
        />
        {/* @ts-ignore */}
        <TextFieldWithOptionalLabel
          className={`mb-0 ${contactInfoType === ContactInfo.none ? '' : 'hidden'} min-w-[100px] sm:min-w-0`}
          name={`members[${idx}].contactInfoPlaceholder`}
          placeholder={strings.addTeamMemberForm.contactInformationPlaceholder}
          readOnly
          required={contactInfoType === ContactInfo.none}
          validationMessage={strings.teamMembersModal.contactValidation}
        />
        {/* @ts-ignore */}
        <TextFieldWithOptionalLabel
          className={`mb-0 ${contactInfoType === ContactInfo.email ? '' : 'hidden'} min-w-[100px] sm:min-w-0`}
          name={`members[${idx}].email`}
          placeholder={strings.addTeamMemberForm.emailPlaceholder}
          validator={(email: string) => searchDuplicate('email', idx) && validateEmail(email)}
          customValidatorMessage={strings.addTeamMemberForm.uniqueValidValsRequired(strings.addTeamMemberForm.email)}
          required={contactInfoType === ContactInfo.email}
          validationMessage={strings.teamMembersModal.emailValidation}
        />
        <PhoneNumberField
          className={`mt-1 mb-0 ${contactInfoType === ContactInfo.phone ? '' : 'hidden'} min-w-[120px] sm:min-w-0`}
          id={`members[${idx}].phoneNumber`}
          name={`members[${idx}].phoneNumber`}
          validator={(v: PhoneNumberValue) =>
            searchDuplicate('phoneNumber', idx) && (!v.number || validatePhone(v.number, v.region))
          }
          customValidatorMessage={strings.addTeamMemberForm.uniqueValidValsRequired(
            strings.addTeamMemberForm.phoneNumber
          )}
          required={contactInfoType === ContactInfo.phone}
          validationMessage={strings.teamMembersModal.telephoneValidation}
        />
      </div>
      {/* @ts-ignore */}
      <SelectFieldCustom
        id="role"
        name={`members[${idx}].role`}
        required
        options={roleOptions}
        className="mt-1 min-w-[100px]"
      />
      <BigCheckbox onChange={() => {}} register={methods.register(`members[${idx}].sendRegistration`)} />
      <div className="flex gap-2 items-center justify-between mb-2">
        {memberCount > 1 ? <IconButton onClick={handleDelete} Icon={FiTrash} label={strings.common.delete} /> : <div />}
        {memberCount === idx + 1 ? (
          <IconButton onClick={handleAdd} Icon={FiPlus} label={strings.common.add} />
        ) : (
          <div />
        )}
      </div>
    </>
  );
};
